import { AVATAR_ICON_SIZE } from '@/constants/AvatarConstants';
import { classnames, getImageContain } from '@/helpers';

import Icon from '@/components/icons/Icon';
import { IconSize, IconVariant } from '@/components/icons/types';
import { MerchantLogoProps, Size, Variant } from './MerchantLogo.types';

const logoContainerSize: Record<Size, React.CSSProperties> = {
  sm: {
    width: 48,
    height: 48,
  },
  md: {
    width: 56,
    height: 56,
  },
  lg: {
    width: 80,
    height: 80,
  },
};

const logoSize: Record<Size, { width: number; height: number }> = {
  sm: { width: 38, height: 38 },
  md: { width: 42, height: 42 },
  lg: { width: 60, height: 60 },
};

const baseIconWrapper =
  'absolute -bottom-xxs -right-xxs z-10 flex items-center justify-center rounded-full bg-bg_secondary border border-border_regular';

const badgeStyles: Record<Size, { iconSize: IconSize; wrapper: string }> = {
  sm: { iconSize: 'xs', wrapper: classnames(baseIconWrapper, 'w-xl h-xl') },
  md: { iconSize: 'xs', wrapper: classnames(baseIconWrapper, 'w-xl h-xl') },
  lg: { iconSize: 'sm', wrapper: classnames(baseIconWrapper, 'w-2xl h-2xl') },
};

const getThumbnailImageUrl = (url: string, size: Size, slug: string) => {
  let dimensions = `${logoSize[size].width}x${logoSize[size].height}`;
  return getImageContain(url, dimensions, slug);
};

const Badge = ({ size, variant }: { size: Size; variant: Variant }) => {
  const { iconSize, wrapper } = badgeStyles[size];
  switch (variant) {
    case 'edit':
      return (
        <span className={classnames(wrapper)}>
          <Icon color="fg_secondary" size={iconSize} variant="pen" />
        </span>
      );
    case 'favorite':
      return (
        <span className={classnames(wrapper)}>
          <Icon color="fg_highlight" size={iconSize} variant="heart-filled" />
        </span>
      );
    case 'check':
      return (
        <span className={classnames(wrapper)}>
          <Icon color="fg_positive" size={iconSize} variant="check" />
        </span>
      );
    case 'canceled':
      return (
        <span className={classnames(wrapper)}>
          <Icon color="fg_negative" size={iconSize} variant="calendar-cross" />
        </span>
      );
    case 'old':
      return (
        <span className={classnames(wrapper)}>
          <Icon color="fg_secondary" size={iconSize} variant="clock" />
        </span>
      );
    case 'default':
      return null;
    default:
      const never: never = variant;
      console.error('Invalid MerchantLogo badge variant', never);
  }
};

const MerchantLogo = ({
  variant = 'default',
  size = 'lg',
  alt,
  imgUrl,
  icon,
  cornerRadius = 'rounded-sm',
}: MerchantLogoProps & { icon?: IconVariant; cornerRadius?: string }) => {
  return (
    <div className="relative">
      <div
        className={classnames(
          'flex items-center justify-center',
          cornerRadius,
          imgUrl && 'border-border_regular bg-surface_l2 border',
          !imgUrl && 'bg-surface_l4',
        )}
        style={logoContainerSize[size]}>
        {imgUrl && (
          <img
            src={getThumbnailImageUrl(imgUrl, size, alt)}
            alt={alt}
            loading="lazy"
            className={classnames('relative z-10', cornerRadius)}
            style={{ ...logoSize[size], objectFit: 'contain', display: 'block' }}
            onError={(e) => (e.currentTarget.src = '/images/bd-fallback-img60x60.jpg')}
          />
        )}
        {!imgUrl && <Icon variant={icon ?? 'shop'} color="fg_secondary" size={AVATAR_ICON_SIZE[size]} />}
      </div>
      {variant !== 'default' && <Badge size={size} variant={variant} />}
    </div>
  );
};

export default MerchantLogo;
